import { Highcharts, Charts } from "components/data-viz";
import {
  stat,
  toTitleCase,
  hexToR,
  hexToG,
  hexToB,
  matchDictionaryTerms,
  hasColor,
  sortSeries,
  countDecimals,
  getValue,
  matchDateFormat,
  sliceIntoChunks,
} from "helpers/data-viz";

/* eslint-disable */
class Chart {
  constructor(chart, index) {
    this.gsheet_ = chart;
    this.index_ = index;
    this.id_ = chart.id;
    this.title_ = chart.title;
    this.dom_ = chart.dom;
    this.data_ = chart.data;
    this.userOptions = {};
    this.userOptions.bell = false;
    this.userOptions.pattern = false;
    this.userOptions.activeCollection = false;
    this.userOptions.dates = [];
    this._init();
  }

  _init() {
    this._setOptions()
      ._getSeries()
      ._extendTemplate()
      ._setSeries()
      ._initializeChart()
      ._addFilters();
  }

  _setOptions() {
    const self = this;
    const row = this.gsheet_.data[0];
    const data = this.data_.sort(sortSeries);

    // self.dom_.setAttribute("id", this.id_);

    let type = getValue(row, "type");
    this.userOptions.type = Highcharts.defined(type) ? type : "column";

    let subtitle = getValue(row, "subtitle");
    this.userOptions.subtitle = Highcharts.defined(subtitle) ? subtitle : false;

    let axisMax = getValue(row, "max");
    this.userOptions.axisMax = Highcharts.defined(axisMax) ? axisMax : false;

    let source = getValue(row, "source");
    let note = getValue(row, "note") || getValue(row, "notes");
    this.userOptions.captionText =
      (Highcharts.defined(source)
        ? "<strong>Source:</strong> ".concat(source, "<br>")
        : ""
      ).concat(
        Highcharts.defined(note) ? "<strong>Note:</strong> ".concat(note) : ""
      ) || "";

    let defaultgroup = getValue(row, "defaultgroup");
    this.userOptions.defaultgroup = Highcharts.defined(defaultgroup)
      ? defaultgroup
      : false;

    let defaulttoggle = getValue(row, "defaulttoggle");
    this.userOptions.defaulttoggle = Highcharts.defined(defaulttoggle)
      ? defaulttoggle
      : false;

    let decimals = getValue(row, "decimal") || getValue(row, "decimals");
    this.userOptions.decimals = Highcharts.defined(decimals) ? decimals : false;

    let logarithmic = getValue(row, "logarithmic");
    this.userOptions.logarithmic =
      Highcharts.defined(logarithmic) &&
      (logarithmic === "TRUE" || logarithmic === true)
        ? true
        : false;

    let categorycolors = getValue(row, "categorycolors");
    this.userOptions.categorycolors =
      Highcharts.defined(categorycolors) &&
      (categorycolors === "TRUE" || categorycolors === true)
        ? true
        : false;

    let dataLabels = getValue(row, "datalabels");
    this.userOptions.dataLabels =
      Highcharts.defined(dataLabels) &&
      (dataLabels === "TRUE" || dataLabels === true)
        ? true
        : false;

    let stack = getValue(row, "stack");
    this.userOptions.stack =
      Highcharts.defined(stack) && (stack === "TRUE" || stack === true)
        ? true
        : false;

    let stacking = getValue(row, "stacking");
    this.userOptions.stacking =
      Highcharts.defined(stacking) &&
      String(stacking).toLowerCase() === "percent"
        ? "percent"
        : "normal";

    let percent = getValue(row, "percent");
    this.userOptions.percent =
      Highcharts.defined(percent) && (percent === "TRUE" || percent === true)
        ? true
        : false;

    let ordinal = getValue(row, "ordinal");
    this.userOptions.ordinal =
      Highcharts.defined(ordinal) && (ordinal === "TRUE" || ordinal === true)
        ? true
        : false;

    let currency = getValue(row, "currency");
    this.userOptions.currency =
      Highcharts.defined(currency) && (currency === "TRUE" || currency === true)
        ? true
        : false;

    let groups =
      Array.from(new Set(data.map((c) => c.group || c.Group))).filter(
        (t) => t
      ) || false;
    this.userOptions.groups = groups && groups[0] ? groups : false;

    let toggles =
      Array.from(new Set(data.map((c) => c.toggle || c.Toggle))).filter(
        (t) => t
      ) || false;
    this.userOptions.toggles = toggles && toggles[0] ? toggles : false;

    let yAxis =
      Array.from(data.map((c) => c.yaxis || c.yAxis)).filter((t) => t) || false;
    this.userOptions.yAxis = yAxis && yAxis[0] ? yAxis : false;

    let yAxisTitles =
      Array.from(
        new Set(data.map((c) => c["axis title"] || c["Axis Title"]))
      ).filter((t) => t) || false;
    this.userOptions.yAxisTitles =
      yAxisTitles && yAxisTitles[0] ? yAxisTitles : false;

    let yAxisColors =
      Array.from(data.map((c) => c["axis color"] || c["Axis Color"])).filter(
        (t) => t
      ) || false;
    this.userOptions.yAxisColors =
      yAxisColors && yAxisColors[0] ? yAxisColors : false;

    let axisColors =
      Array.from(
        new Set(data.map((c) => c["axis color"] || c["Axis Color"]))
      ).filter((t) => t) || false;
    this.userOptions.axisColors =
      axisColors && axisColors[0] ? axisColors : false;

    let labels =
      Array.from(new Set(data.map((c) => c["label"] || c["Label"]))).filter(
        (t) => t
      ) || false;
    this.userOptions.labels = labels && labels[0] ? labels : false;

    let categoryStats =
      Array.from(
        new Set(data.map((c) => c["category stats"] || c["Category Stats"]))
      ).filter((t) => t) || false;
    this.userOptions.categoryStats =
      categoryStats && categoryStats[0] ? categoryStats : false;

    this.userOptions.includeStats = false;

    let xAxis =
      Array.from(data.map((c) => c.xaxis || c.xAxis)).filter((t) => t) || false;
    this.userOptions.xAxis =
      xAxis && xAxis[0] ? xAxis[0].split(",").flat() : [];

    this.userOptions.categories = Object.keys(row).filter(
      (c) =>
        !matchDictionaryTerms("config").test(c) &&
        !this.userOptions.xAxis.includes(c)
    );

    if (this.userOptions.categoryStats) {
      let matchCounty = this.userOptions.categoryStats.filter((c) =>
        matchDictionaryTerms("county").test(c)
      );
      let matchRace = this.userOptions.categoryStats.filter((c) =>
        matchDictionaryTerms("races").test(c)
      );
      if (matchCounty && matchCounty[0]) {
        self.userOptions.includeStats = ["harris", "fort bend", "montgomery"];
      } else if (matchRace && matchRace[0]) {
        self.userOptions.includeStats = [
          "asian",
          "black",
          "hispanic",
          "white",
          "unknown",
          "other",
        ];
      } else {
        this.userOptions.categoryStats.forEach(function(c) {
          if (c.toLowerCase() === "county" || c.toLowerCase() === "counties") {
            self.userOptions.includeStats = [
              "harris",
              "fort bend",
              "montgomery",
            ];
          } else if (
            c.toLowerCase() === "all" ||
            c.toLowerCase() === "TRUE" ||
            c === true
          ) {
            let filterArr = self.userOptions.categories.map((c) =>
              String(c).toLowerCase()
            );
            self.userOptions.includeStats = filterArr;
          } else {
            self.userOptions.includeStats = [];
            self.userOptions.includeStats.push(c.toLowerCase());
          }
        });
      }
    }
    let datetime = this.userOptions.categories.some((c) => matchDateFormat(c));
    this.userOptions.datetime =
      this.userOptions.type === "spline" ||
      this.userOptions.type === "line" ||
      this.userOptions.type === "area"
        ? datetime
        : false;
    return this;
  }

  _getSeries() {
    const self = this;
    let values = [];
    let allValues = [];

    this.userOptions.series = Array.from(
      new Set(self.data_.map((s) => s.undefined))
    ).map(function(x) {
      const series = {};
      const data = Array.from(
        self.data_.map(function(c, i, a) {
          if (c.undefined === x) {
            const obj = {};
            const entries = Array.from(
              Object.entries(c).filter(
                (k, v) => !matchDictionaryTerms("config").test(k[0])
              )
            );
            const toggle = Highcharts.defined(c.toggle || c.Toggle)
              ? c.toggle || c.Toggle
              : false;
            const group = Highcharts.defined(c.group || c.Group)
              ? c.group || c.Group
              : false;
            const type = Highcharts.defined(c.type || c.Type)
              ? c.type || c.Type
              : false;
            const hidden = Highcharts.defined(c.hidden || c.Hidden)
              ? c.hidden || c.Hidden
              : false;
            const regression = Highcharts.defined(c.regression || c.Regression)
              ? c.regression || c.Regression
              : false;
            const yAxisType = Highcharts.defined(c.yaxis || c.yAxis)
              ? c.yaxis || c.yAxis
              : false;
            const axisColor = Highcharts.defined(
              c["axis color"] || c["Axis Color"]
            )
              ? c["axis color"] || c["Axis Color"]
              : false;
            const pattern = Highcharts.defined(c["pattern"] || c["Pattern"])
              ? c["pattern"] || c["Pattern"]
              : false;
            if (toggle) {
              obj.set = String(toggle).toLowerCase();
              if (self.userOptions.defaulttoggle)
                series.defaultToggle = String(
                  self.userOptions.defaulttoggle
                ).toLowerCase();
            }
            if (group) {
              series.group = String(group).toLowerCase();
              if (self.userOptions.defaultgroup)
                series.defaultGroup = String(
                  self.userOptions.defaultgroup
                ).toLowerCase();
            }
            if (hidden) {
              series.hidden =
                hidden === "TRUE" || hidden === true ? true : false;
            }
            if (type) {
              obj.type = type;
            } else {
              obj.type = self.userOptions.type;
            }
            if (regression) {
              obj.regression =
                regression === "TRUE" || regression === true ? true : false;
            }
            obj.values = [];
            if (obj.type === "errorbar") {
              entries.forEach(function(e) {
                let arr = e[1].split(":");
                if (
                  arr[0].toString().match(/\d+(\%)/g) ||
                  self.userOptions.percent
                ) {
                  obj.values.push([
                    Highcharts.correctFloat(parseFloat(arr[0]) * 100),
                    Highcharts.correctFloat(parseFloat(arr[1]) * 100),
                  ]);
                } else {
                  obj.values.push([parseFloat(arr[0]), parseFloat(arr[1])]);
                }
              });
            } else {
              entries.forEach(function(e, i) {
                let entry = {};
                const category = e[0];
                if (self.userOptions.categorycolors) {
                  entry.color =
                    hasColor(String(category)) || hasColor(String(x));
                }
                if (obj.set) entry.name = toTitleCase(obj.set.toString());
                if (Highcharts.defined(e[0])) {
                  entry.category = e[0];
                  if (self.userOptions.datetime) {
                    let date = matchDateFormat(e[0].toString());
                    entry.category = date;
                    entry.x =
                      date.length > 1
                        ? Highcharts.time.makeTime(date[2], date[3], date[4])
                        : Highcharts.time.makeTime(date);
                    self.userOptions.dates.push(entry.x);
                  }
                }
                if (pattern && (pattern === "TRUE" || pattern === true)) {
                  entry.color = {};
                  entry.color.pattern = {
                    path: {
                      d: "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11",
                      strokeWidth: 2,
                    },
                    width: 10,
                    height: 10,
                    opacity: 0.6,
                    color: axisColor || undefined,
                  };
                }
                if (
                  !Highcharts.defined(e[1]) ||
                  e[1] === 0 ||
                  String(e[1]).toLowerCase() === "n/a" ||
                  String(e[1]).toLowerCase() === "na" ||
                  String(e[1]).toLowerCase() === "null"
                ) {
                  entry.y = null;
                  series.connectNulls = true;
                } else if (String(e[1]).toLowerCase() === "skip") {
                  entry.y = "skip";
                } else {
                  let value = String(e[1]);
                  let roundPercent = true;
                  if (value.charAt(0) === "$") self.userOptions.currency = true;
                  if (value.match(/\d+(\%)/g)) {
                    self.userOptions.percent = true;
                    roundPercent = false;
                  }
                  if (self.userOptions.percent) series.post = "%";
                  if (self.userOptions.currency) series.pre = "$";
                  if (yAxisType) {
                    series.tooltip = {};
                    if (yAxisType === "percent") {
                      series.className = "percent";
                      series.tooltip.valueSuffix = "%";
                    }
                    if (yAxisType === "currency") {
                      series.className = "currency";
                      series.tooltip.valuePrefix = "$";
                    }
                    if (yAxisType === "number") {
                      series.className = "number";
                    }
                    if (self.userOptions.yAxis.indexOf(yAxisType) === 1) {
                      series.yAxis = 1;
                    }
                  }
                  if (self.userOptions.xAxis.includes(category)) {
                    series.xAxis = 1;
                  }
                  let calc = parseFloat(
                    value.replace("$", "").replace(",", "")
                  );
                  entry.y =
                    (yAxisType === "percent" || self.userOptions.percent) &&
                    roundPercent
                      ? Math.round(Highcharts.correctFloat(calc * 100) * 100) /
                        100
                      : Math.round(calc * 100) / 100;
                  if (!self.userOptions.decimals) {
                    let decimals = countDecimals(entry.y);
                    self.userOptions.decimals =
                      decimals > self.userOptions.decimals ? decimals : 0;
                  }
                }
                if (entry.y !== "skip") {
                  allValues.push(Number(entry.y));
                  obj.values.push(entry);
                }
                if (self.userOptions.includeStats) {
                  if (entry.category && typeof entry.y === "number") {
                    let categoryStats = self.userOptions.includeStats;
                    if (
                      categoryStats.some(
                        (o) => o === String(entry.category).toLowerCase()
                      )
                    ) {
                      if (
                        values.some(
                          (o) =>
                            o.category === String(entry.category).toLowerCase()
                        )
                      ) {
                        values.forEach(function(e) {
                          if (
                            e.category === String(entry.category).toLowerCase()
                          ) {
                            e.values.push(entry.y);
                          }
                        });
                      } else {
                        values.push({
                          category: String(entry.category).toLowerCase(),
                          values: [entry.y],
                        });
                      }
                    }
                  }
                }
              });
            }
            return obj;
          }
        })
      );
      series.name = x;
      if (series.name === "Annotate") {
        series.visible = false;
        series.enableMouseTracking = false;
        series.showInLegend = false;
      }
      series.id = Highcharts.uniqueKey();
      series.color = hasColor(x);
      series.data = Array.from(data.filter((t) => t)).flat();
      return series;
    });

    const v = Array.from(
      new Set(allValues.filter((v) => typeof v === "number"))
    ).flat();
    self.userOptions.min = stat.min([...v]);
    self.userOptions.max = stat.max([...v]);
    self.userOptions.modes = stat.modes([...v]);
    self.userOptions.zScores = stat.zScores([...v]);
    self.userOptions.range = Highcharts.correctFloat(stat.range([...v]));
    self.userOptions.midrange = Highcharts.correctFloat(stat.midrange([...v]));
    self.userOptions.sum = Highcharts.correctFloat(stat.sum([...v]));
    self.userOptions.mean = Highcharts.correctFloat(stat.mean([...v]));
    self.userOptions.median = Highcharts.correctFloat(stat.median([...v]));
    self.userOptions.variance = Highcharts.correctFloat(stat.variance([...v]));
    self.userOptions.standardDeviation = Highcharts.correctFloat(
      stat.standardDeviation([...v])
    );
    self.userOptions.meanAbsoluteDeviation = Highcharts.correctFloat(
      stat.meanAbsoluteDeviation([...v])
    );
    return this;
  }

  _extendTemplate() {
    const self = this;
    let wrapper = Highcharts.createElement(
      "div",
      { className: "chart" },
      {},
      self.dom_
    );
    let filtersContainer = Highcharts.createElement(
      "div",
      { className: "data-viz-heading" },
      {},
      wrapper
    );
    let titlesContainer = Highcharts.createElement(
      "div",
      { className: "data-viz-titles" },
      {},
      filtersContainer
    );

    self.options = {
      chart: { style: {} },
      title: { style: { display: "none" } },
      subtitle: { style: { display: "none" } },
      plotOptions: { series: { dataLabels: {} } },
      tooltip: {},
      legend: {},
      caption: { style: { display: "none" } },
      xAxis: [{}],
    };
    self.options.categories = self.userOptions.categories;
    self.options.chart.renderTo = Highcharts.createElement(
      "div",
      { className: "chart", id: self.id_ },
      {},
      wrapper
    );
    self.options.title.text = self.title_;
    self.options.chart.type = self.userOptions.type;
    self.options.tooltip.valueDecimals = self.userOptions.decimals;

    if (self.userOptions.subtitle) {
      titlesContainer.innerHTML = `<h4 class='heading'>${self.title_}</h4><p>${self.userOptions.subtitle}</p>`;
      self.options.subtitle.text = self.userOptions.subtitle;
    } else {
      titlesContainer.innerHTML = `<h4 class='heading'>${self.title_}</h4>`;
    }

    self.options.caption.text =
      '<svg style="margin-bottom: 10px" width="112px" height="30px" viewBox="0 0 112 30" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g>' +
      '<path d="M5.21914358,13.3333333 L5.21914358,28.6487174 C6.1382917,29.08453 7.16824732,29.4053058 8.30956447,29.5911345 C9.28688653,29.7498632 10.3434359,29.8355878 11.4071878,29.8532858 C11.6952873,29.8571572 11.9844949,29.8543919 12.2720403,29.8483082 L12.2720403,13.3333333 L5.21914358,13.3333333 Z" fill="#EF233C"></path>' +
      '<path d="M16.4727178,2.87192941 L16.4727178,8.92740568 L18.0098393,8.92740568 L18.0098393,28.8139584 C19.5405783,28.0088058 20.7463411,26.8537787 21.6122351,25.4680774 C22.6892839,23.7429868 23.2434986,21.6647107 23.2434986,19.4694419 L23.2434986,2.87192941 L16.4727178,2.87192941 Z" fill="#112E51"></path>' +
      '<path d="M6.74005017,0.0712322106 L6.74005017,13.5047277 L5.23537433,13.5041785 L5.23537433,28.7144897 C1.86977541,27.0282998 0.10989322,23.580124 0.10989322,19.2976412 L0.10989322,0.0712322106 L6.74005017,0.0712322106 Z" fill="#DDD12A"></path>' +
      '<path d="M11.3946321,8.81395839 L11.3946321,13.3690736 L12.2254224,13.3690736 L12.2254224,29.8284511 C13.2471561,29.8091592 14.2582523,29.728684 15.1916946,29.574348 C16.2900184,29.3930033 17.2814352,29.0876386 18.165413,28.6808531 L18.165413,28.1792613 L18.165413,8.81395839 L11.3946321,8.81395839 Z" fill="#7AC896"></path>' +
      '<path d="M89.9709647,10.3015087 C88.6115601,10.3015087 87.7639252,9.25302546 87.7639252,7.89275646 C87.7639252,6.5330393 88.6115601,5.4845561 89.9709647,5.4845561 C91.3176149,5.4845561 92.3034223,6.5330393 92.3034223,7.89275646 C92.3034223,9.25302546 91.3176149,10.3015087 89.9709647,10.3015087 L89.9709647,10.3015087 Z M92.2656906,5.87304671 L92.2407133,5.87304671 C91.7044978,5.05688532 90.7441991,4.70702303 89.9709647,4.70702303 C88.1497452,4.70702303 86.8652726,6.06674019 86.8652726,7.89275646 C86.8652726,9.71877273 88.1497452,11.0784899 89.9709647,11.0784899 C90.7441991,11.0784899 91.7044978,10.7286276 92.2407133,9.91301804 L92.2656906,9.91301804 L92.2656906,10.9234248 L93.0888797,10.9234248 L93.0888797,1.13279897 L92.2656906,1.13279897 L92.2656906,5.87304671 Z M77.6257856,8.34636341 C77.6257856,9.42078277 77.051307,10.3015087 75.8545208,10.3015087 C75.1934187,10.3015087 74.4823619,9.99137837 74.4823619,9.18790913 C74.4823619,7.97056495 76.3652275,7.90544863 77.2017024,7.90544863 L77.6257856,7.90544863 L77.6257856,8.34636341 Z M78.4484433,6.85696542 C78.4484433,5.38025961 77.4631673,4.70702303 76.3158043,4.70702303 C75.218396,4.70702303 74.3824525,5.01825699 73.8462371,5.58774892 L74.3452523,6.19642101 C74.8442675,5.7301219 75.4426606,5.4845561 76.12874,5.4845561 C77.1389934,5.4845561 77.6257856,6.0021757 77.6257856,7.0506589 L77.6257856,7.20682771 L77.2139254,7.20682771 C75.8167891,7.20682771 73.5842408,7.3359567 73.5842408,9.30434595 C73.5842408,10.4830618 74.5572939,11.0784899 75.5925245,11.0784899 C76.5278459,11.0784899 77.1767251,10.7418716 77.6502315,9.93895421 L77.6752089,9.93895421 C77.6752089,10.2628803 77.7001862,10.6254348 77.7501409,10.9234248 L78.5483526,10.9234248 C78.4983979,10.5994986 78.4484433,10.0300067 78.4484433,9.56315575 L78.4484433,6.85696542 Z M83.1415245,4.70702303 C82.3682901,4.70702303 81.5456324,5.17332213 81.1837268,5.83331472 L81.1587495,5.83331472 C81.1587495,5.50994043 81.1587495,5.1860143 81.1215492,4.86264001 L80.2733828,4.86264001 C80.2983601,5.30245112 80.3355604,5.87304671 80.3355604,6.19642101 L80.3355604,10.9234248 L81.1587495,10.9234248 L81.1587495,7.81494797 C81.1587495,5.60044108 82.755173,5.4845561 82.9300143,5.4845561 C84.1523093,5.4845561 84.5136834,6.22180534 84.5136834,7.56938217 L84.5136834,10.9234248 L85.3368725,10.9234248 L85.3368725,7.07659506 C85.3368725,5.57505675 84.6258157,4.70702303 83.1415245,4.70702303 L83.1415245,4.70702303 Z M95.0248886,10.9234248 L95.8480777,10.9234248 L95.8480777,4.86264001 L95.0248886,4.86264001 L95.0248886,10.9234248 Z M107.410988,10.1464435 C106.189224,10.1464435 105.241149,9.08416448 105.241149,7.81494797 C105.241149,6.54573146 106.189224,5.4845561 107.410988,5.4845561 C108.783147,5.4845561 109.61909,6.54573146 109.61909,7.81494797 C109.668514,9.2138453 108.608306,10.1464435 107.410988,10.1464435 L107.410988,10.1464435 Z M109.594113,4.86264001 L109.594113,5.87304671 L109.569136,5.87304671 C108.895279,4.9272045 108.009913,4.70702303 107.31161,4.70702303 C105.590831,4.70702303 104.343028,6.07998419 104.343028,7.84088413 C104.343028,9.61557991 105.702964,10.9234248 107.386542,10.9234248 C108.234177,10.9234248 109.045143,10.5994986 109.569136,9.78388906 L109.594113,9.78388906 L109.594113,10.8715524 C109.594113,12.1793973 108.995189,13.2543685 107.323833,13.2543685 C106.389043,13.2543685 105.578077,12.8140055 105.091285,12.1407689 L104.480669,12.8140055 C105.228926,13.6428591 106.126515,14.0313497 107.286633,14.0313497 C109.905533,14.0313497 110.417302,12.1926413 110.417302,10.8715524 L110.417302,4.86264001 L109.594113,4.86264001 Z M95.4362175,1.90978021 C95.1370209,1.90978021 94.8372929,2.16914184 94.8372929,2.5316963 C94.8372929,2.89369892 95.1370209,3.15306055 95.4362175,3.15306055 C95.735414,3.15306055 96.0346106,2.89369892 96.0346106,2.5316963 C96.0346106,2.16914184 95.735414,1.90978021 95.4362175,1.90978021 L95.4362175,1.90978021 Z M100.593239,4.70702303 C99.8200049,4.70702303 98.9968158,5.17332213 98.6354416,5.83331472 L98.6104643,5.83331472 C98.6104643,5.50994043 98.6104643,5.1860143 98.573264,4.86264001 L97.7250976,4.86264001 C97.750075,5.30245112 97.7872752,5.87304671 97.7872752,6.19642101 L97.7872752,10.9234248 L98.6104643,10.9234248 L98.6104643,7.81494797 C98.6104643,5.60044108 100.206888,5.4845561 100.381729,5.4845561 C101.604024,5.4845561 101.965398,6.22180534 101.965398,7.56938217 L101.965398,10.9234248 L102.788587,10.9234248 L102.788587,7.07659506 C102.788587,5.57505675 102.077531,4.70702303 100.593239,4.70702303 L100.593239,4.70702303 Z M71.7656785,10.3015087 C71.2539089,10.3015087 70.917512,10.1205074 70.917512,9.2138453 L70.917512,5.63962124 L72.6010905,5.63962124 L72.6010905,4.86264001 L70.917512,4.86264001 L70.917512,3.15306055 L70.0948544,3.15306055 L70.0948544,4.86264001 L68.8592736,4.86264001 L68.8592736,5.63962124 L70.0948544,5.63962124 L70.0948544,9.45996293 C70.0948544,10.7937439 70.917512,11.0784899 71.5408825,11.0784899 C71.9527427,11.0784899 72.3518486,11.0012332 72.6510452,10.8588603 L72.6138449,10.068635 C72.3640716,10.211008 72.0521206,10.3015087 71.7656785,10.3015087 L71.7656785,10.3015087 Z M47.8400518,10.3015087 C46.4806473,10.3015087 45.6324808,9.25302546 45.6324808,7.89275646 C45.6324808,6.5330393 46.4806473,5.4845561 47.8400518,5.4845561 C49.186702,5.4845561 50.171978,6.5330393 50.171978,7.89275646 C50.171978,9.25302546 49.186702,10.3015087 47.8400518,10.3015087 L47.8400518,10.3015087 Z M50.1347777,5.87304671 L50.1098004,5.87304671 C49.5735849,5.05688532 48.6132862,4.70702303 47.8400518,4.70702303 C46.0188323,4.70702303 44.7343598,6.06674019 44.7343598,7.89275646 C44.7343598,9.71877273 46.0188323,11.0784899 47.8400518,11.0784899 C48.6132862,11.0784899 49.5735849,10.7286276 50.1098004,9.91301804 L50.1347777,9.91301804 L50.1347777,10.9234248 L50.9579668,10.9234248 L50.9579668,1.13279897 L50.1347777,1.13279897 L50.1347777,5.87304671 Z M53.3882081,7.42645735 C53.3882081,6.42929464 54.3607297,5.4845561 55.4331607,5.4845561 C56.6182553,5.4845561 57.3282493,6.2742295 57.3282493,7.42645735 L53.3882081,7.42645735 Z M55.4331607,4.70702303 C53.7745596,4.70702303 52.490087,6.06674019 52.490087,7.89275646 C52.490087,9.71877273 53.7745596,11.0784899 55.5080927,11.0784899 C56.4683914,11.0784899 57.3282493,10.7032433 58.0143288,9.79658122 L57.3787354,9.25302546 C57.0545615,9.79658122 56.380705,10.3015087 55.5080927,10.3015087 C54.3479753,10.3015087 53.4626086,9.34352611 53.3626993,8.12618193 L58.2269018,8.12618193 L58.2269018,7.73769131 C58.2269018,6.07998419 57.2038941,4.72026702 55.4331607,4.70702303 L55.4331607,4.70702303 Z M66.6469198,7.55613817 L65.8115077,7.34864886 C65.0504963,7.16764755 64.6641448,6.96015824 64.6641448,6.39066632 C64.6641448,5.74281406 65.2997381,5.4845561 65.8736853,5.4845561 C66.4720784,5.4845561 66.8839387,5.80737856 67.1331806,6.30016566 L67.8692147,5.79468639 C67.4950862,5.00446116 66.7340747,4.70702303 65.92364,4.70702303 C64.8262317,4.70702303 63.7660237,5.27706679 63.7660237,6.5330393 C63.7660237,7.68581899 64.863432,8.02243728 65.2625379,8.12618193 L66.0607496,8.31987541 C66.6718971,8.47549239 67.1709123,8.68242986 67.1709123,9.29165379 C67.1709123,9.99137837 66.4720784,10.3015087 65.7992848,10.3015087 C65.1132053,10.3015087 64.6019671,9.93895421 64.2650389,9.39484661 L63.5539821,9.89977404 C64.1024205,10.7545638 64.9133866,11.0784899 65.7992848,11.0784899 C66.9338934,11.0784899 68.0685019,10.5349341 68.0685019,9.2138453 C68.0685019,8.35905557 67.4578859,7.75038348 66.6469198,7.55613817 L66.6469198,7.55613817 Z M41.011143,4.70702303 C40.2379086,4.70702303 39.4147195,5.17332213 39.0528139,5.83331472 L39.028368,5.83331472 C39.028368,5.50994043 39.028368,5.1860143 38.9906363,4.86264001 L38.1424699,4.86264001 C38.1674473,5.30245112 38.2046475,5.87304671 38.2046475,6.19642101 L38.2046475,10.9234248 L39.028368,10.9234248 L39.028368,7.81494797 C39.028368,5.60044108 40.6247915,5.4845561 40.7991014,5.4845561 C42.0213964,5.4845561 42.3827706,6.22180534 42.3827706,7.56938217 L42.3827706,10.9234248 L43.2059596,10.9234248 L43.2059596,7.07659506 C43.2059596,5.57505675 42.4954343,4.70702303 41.011143,4.70702303 L41.011143,4.70702303 Z M36.285113,4.86264001 L35.4619239,4.86264001 L35.4619239,7.97056495 C35.4619239,10.1850718 33.8655004,10.3015087 33.6911905,10.3015087 C32.4683642,10.3015087 32.10699,9.56315575 32.10699,8.21668259 L32.10699,4.86264001 L31.2838009,4.86264001 L31.2838009,8.70836603 C31.2838009,10.211008 31.9948577,11.0784899 33.4791489,11.0784899 C34.2518519,11.0784899 35.0755724,10.6121908 35.4369466,9.95164637 L35.4619239,9.95164637 C35.4619239,10.2755725 35.4619239,10.5994986 35.4991242,10.9234248 L36.3472906,10.9234248 C36.3223133,10.4830618 36.285113,9.91301804 36.285113,9.58964375 L36.285113,4.86264001 Z M60.5678624,5.98948353 L60.542885,5.98948353 L60.542885,4.86264001 L59.7324503,4.86264001 C59.7568962,5.74281406 59.7818736,6.20911317 59.7818736,6.75322077 L59.7818736,10.9234248 L60.6055941,10.9234248 L60.6055941,7.50481768 C60.6055941,6.68865628 61.1667869,5.56181275 62.4140592,5.56181275 C62.6383237,5.56181275 62.7881877,5.58774892 62.8875656,5.62637724 L63.050184,4.78483152 C62.8753427,4.73295919 62.6760554,4.70702303 62.4385051,4.70702303 C61.4165602,4.70702303 60.8048813,5.34163128 60.5678624,5.98948353 L60.5678624,5.98948353 Z" fill="#112E51"></path>' +
      '<path d="M77.275333,19.4288644 L80.5866384,19.4288644 L80.5866384,29.0891107 L83.143758,29.0891107 L83.143758,19.4288644 L86.4545327,19.4288644 L86.4545327,17.0895308 L77.275333,17.0895308 L77.275333,19.4288644 Z M71.0672325,20.4627149 C71.0672325,19.5644064 72.0671522,19.2252771 72.7884319,19.2252771 C73.4115242,19.2252771 74.2309913,19.4798984 74.6407249,20.0220663 L76.3948304,18.039422 C75.4108329,17.1405648 74.1487261,16.7849729 72.837791,16.7849729 C70.5593114,16.7849729 68.4119254,18.12393 68.4119254,20.6827648 C68.4119254,24.682076 73.8541638,23.496221 73.8541638,25.6994639 C73.8541638,26.5467384 72.9195254,26.9533643 72.0836053,26.9533643 C71.231232,26.9533643 70.3788588,26.5297271 69.8544848,25.8179946 L68.0350979,27.8856955 C69.1332051,28.9360086 70.444671,29.3936685 71.9360587,29.3936685 C74.3291788,29.3936685 76.4107527,28.1227568 76.4107527,25.4453913 C76.4107527,21.2424928 71.0672325,22.6489465 71.0672325,20.4627149 L71.0672325,20.4627149 Z M93.0713053,26.9533643 C90.8910131,26.9533643 89.4649068,25.326312 89.4649068,23.0895951 C89.4649068,20.8523295 90.8910131,19.2252771 93.0713053,19.2252771 C95.2510666,19.2252771 96.6777037,20.8523295 96.6777037,23.0895951 C96.6777037,25.326312 95.2510666,26.9533643 93.0713053,26.9533643 L93.0713053,26.9533643 Z M93.0713053,16.7849729 C89.4489845,16.7849729 86.8095997,19.2422884 86.8095997,23.0895951 C86.8095997,26.9369018 89.4489845,29.3936685 93.0713053,29.3936685 C96.693626,29.3936685 99.3330108,26.9369018 99.3330108,23.0895951 C99.3330108,19.2422884 96.693626,16.7849729 93.0713053,16.7849729 L93.0713053,16.7849729 Z M109.412351,17.0895308 L109.412351,25.42838 L109.379445,25.42838 L104.445658,17.0895308 L100.970884,17.0895308 L100.970884,29.0891107 L103.527473,29.0891107 L103.527473,20.5472228 L103.560379,20.5472228 L108.625259,29.0891107 L111.968939,29.0891107 L111.968939,17.0895308 L109.412351,17.0895308 Z M64.2790299,24.3605068 C64.2790299,25.8350059 63.2955632,26.9533643 61.9511912,26.9533643 C60.590897,26.9533643 59.6074303,25.8350059 59.6074303,24.3605068 L59.6074303,17.0895308 L57.0503106,17.0895308 L57.0503106,24.4620261 C57.0503106,27.2924936 58.722151,29.3936685 61.9511912,29.3936685 C65.1643092,29.3936685 66.8361495,27.2924936 66.8361495,24.4620261 L66.8361495,17.0895308 L64.2790299,17.0895308 L64.2790299,24.3605068 Z M38.8076133,21.5646107 L33.8409205,21.5646107 L33.8409205,17.0895308 L31.2838009,17.0895308 L31.2838009,29.0891107 L33.8409205,29.0891107 L33.8409205,23.9028469 L38.8076133,23.9028469 L38.8076133,29.0891107 L41.3647329,29.0891107 L41.3647329,17.0895308 L38.8076133,17.0895308 L38.8076133,21.5646107 Z M49.2558194,26.9533643 C47.076058,26.9533643 45.6499516,25.326312 45.6499516,23.0895951 C45.6499516,20.8523295 47.076058,19.2252771 49.2558194,19.2252771 C51.4355807,19.2252771 52.8616871,20.8523295 52.8616871,23.0895951 C52.8616871,25.326312 51.4355807,26.9533643 49.2558194,26.9533643 L49.2558194,26.9533643 Z M49.2558194,16.7849729 C45.6334986,16.7849729 42.9946445,19.2422884 42.9946445,23.0895951 C42.9946445,26.9369018 45.6334986,29.3936685 49.2558194,29.3936685 C52.8781401,29.3936685 55.5169942,26.9369018 55.5169942,23.0895951 C55.5169942,19.2422884 52.8781401,16.7849729 49.2558194,16.7849729 L49.2558194,16.7849729 Z" fill="#112E51"></path>' +
      "</g></svg><br />" +
      self.userOptions.captionText;

    let setName =
      self.userOptions.series[0].data[0] &&
      self.userOptions.series[0].data[0].set
        ? toTitleCase(self.userOptions.series[0].data[0].set)
        : false;

    if (setName) {
      if (self.userOptions.subtitle) {
        self.options.subtitle.text =
          self.userOptions.subtitle +
          ' | <span style="text-transform: capitalize">' +
          toTitleCase(String(setName).toLowerCase()) +
          "</span>";
      } else {
        self.options.subtitle.text =
          '<span style="text-transform: capitalize">' + setName + "</span>";
      }
    }
    self.options.chart.marginTop = titlesContainer.offsetHeight + 10;
    // self.options.chart.style.marginTop = titlesContainer.offsetHeight;
    // self.options.chart.style.marginBottom = -(titlesContainer.offsetHeight * 2);
    // self.options.chart.height = (9 / 16 * 100) + '%';

    let footerContainer = Highcharts.createElement(
      "div",
      { className: "data-viz-footer" },
      {},
      wrapper
    );
    let exportContainer = Highcharts.createElement(
      "div",
      { className: "data-viz-export-options" },
      {},
      footerContainer
    );
    let captionContainer = Highcharts.createElement(
      "div",
      { className: "data-viz-caption" },
      {},
      footerContainer
    );
    let captionText = Highcharts.createElement("p", {}, {}, captionContainer);
    captionText.innerHTML = self.userOptions.captionText;
    let downloadImageLink = Highcharts.createElement(
      "a",
      { className: "data-viz-export" },
      {},
      exportContainer
    );
    downloadImageLink.setAttribute("data-title", self.options.title.text);
    downloadImageLink.href = "#";
    downloadImageLink.innerHTML = "Export image";

    downloadImageLink.addEventListener("click", function(e) {
      e.preventDefault();
      self.instance.exportChart({
        type: "image/jpeg",
        filename: this.getAttribute("data-title")
          .toLowerCase()
          .replace(/\W|_/g, "-"),
      });
    });

    if (self.userOptions.yAxis) {
      self.options.yAxis = [
        { gridLineWidth: 1, labels: { style: {} }, title: { style: {} } },
        {
          gridLineWidth: 1,
          labels: { style: {} },
          title: { style: {} },
          opposite: true,
        },
      ];
      self.options.xAxis = [
        {
          gridLineWidth: 0,
          lineWidth: 3,
          lineColor: "#ccd6eb",
          labels: { style: {} },
          title: { style: {} },
        },
      ];
      self.userOptions.yAxis.forEach(function(o, i) {
        self.userOptions.series[i].color = self.userOptions.yAxisColors[i];
        if (i < 2) {
          if (o === "percent") {
            self.options.yAxis[i].labels.format = "{value}%";
            self.options.yAxis[i].min = 0;
          } else if (o === "currency") {
            self.options.yAxis[i].labels.format = "${value:,f}";
          } else {
            self.options.yAxis[i].labels.format = "{value:,f}";
          }
          if (self.userOptions.logarithmic) {
            self.options.yAxis[i].type = "logarithmic";
          }
          if (self.userOptions.stack) {
            self.options.yAxis[i].reversedStacks = false;
            self.options.yAxis[i].reversed = false;
            if (self.userOptions.percent) {
              self.options.yAxis[i].tickAmount = 3;
            }
          }
          // self.userOptions.series[i].color = self.userOptions.yAxisColors[i];
          self.options.yAxis[i].title.enabled = true;
          self.options.yAxis[i].title.text = self.userOptions.yAxisTitles[i];
          self.options.yAxis[i].title.style.color =
            self.userOptions.yAxisColors[i];
          self.options.yAxis[i].labels.style.color =
            self.userOptions.yAxisColors[i];
          self.options.yAxis[i].title.align = "middle";
          self.options.yAxis[i].title.margin = 30;
          self.options.yAxis[i].title.textAlign = "center";
          self.options.yAxis[i].title.offset = undefined;
          self.options.yAxis[i].title.y = undefined;
          if (i === 0) {
            self.options.yAxis[i].title.rotation = 270;
            self.options.yAxis[i].title.textAlign = "center";
            self.options.yAxis[i].title.x = undefined;
            self.options.yAxis[i].labels.align = undefined;
          } else {
            self.options.yAxis[i].title.rotation = 90;
            self.options.yAxis[i].labels.align = "left";
            self.userOptions.series[i].dataLabels = {
              enabled: true,
              y: -5,
              x: 10,
              align: "left",
              style: {
                textOutline: false,
                color: self.userOptions.yAxisColors[i],
                fontSize: "12px",
                fontWeight: "700",
              },
            };
            self.userOptions.series[i].dataLabels.format =
              "{y}% <br />" + self.userOptions.labels[i];
          }
          self.options.yAxis[i].tickAmount = 4;
        }
      });
    } else {
      self.options.yAxis = [
        { gridLineWidth: 1, labels: { style: {} }, title: { style: {} } },
      ];
      self.options.xAxis = [
        {
          gridLineWidth: 0,
          lineWidth: 3,
          lineColor: "#ccd6eb",
          labels: { style: {} },
          title: { style: {} },
        },
      ];

      if (self.userOptions.percent) {
        self.options.tooltip.valueSuffix = "%";
        self.options.yAxis[0].labels.format = "{value}%";
      }

      if (self.userOptions.logarithmic) {
        self.options.yAxis[0].type = "logarithmic";
      }

      if (self.userOptions.currency) {
        self.options.yAxis[0].labels.format = "${value:,f}";
        self.options.tooltip.valuePrefix = "$";
      }

      if (self.userOptions.stack) {
        self.options.yAxis[0].reversedStacks = false;
        self.options.yAxis[0].reversed = false;
      }
    }

    if (self.userOptions.labels || self.userOptions.dataLabels) {
      self.userOptions.series.forEach(function(l, i) {
        l.dataLabels = [];
        l.suffix = "";
        l.prefix = "";
        if (l.hasOwnProperty("tooltip")) {
          if (l.tooltip.hasOwnProperty("valueSuffix")) {
            l.suffix = l.tooltip.valueSuffix;
          } else {
            if (l.hasOwnProperty("post")) {
              l.suffix = l.post;
            }
          }
        } else if (l.hasOwnProperty("post")) {
          l.suffix = l.post;
        } else {
          l.suffix = "";
        }
        l.prefix = "";
        if (l.hasOwnProperty("tooltip")) {
          if (l.tooltip.hasOwnProperty("valuePrefix")) {
            l.prefix = l.tooltip.valuePrefix;
          } else {
            if (l.hasOwnProperty("pre")) {
              l.prefix = l.pre;
            }
          }
        } else if (l.hasOwnProperty("pre")) {
          l.prefix = l.pre;
        } else {
          l.prefix = "";
        }
        if (l.name !== "Annotate") {
          l.data[0].values.forEach(function(d, c) {
            let color =
              typeof d.color === "string" && Highcharts.defined(d.color)
                ? d.color
                : l.color;
            d.dataLabels = {
              color: color,
              style: {
                color: color,
                fontSize: "12px",
                fontWeight: "700",
                textOutline: false,
              },
            };
            l.dataLabels[i] = {
              enabled: true,
              y: 0,
              x: 0,
              align: "left",
              style: {
                textOutline: false,
                color: color,
                fontSize: "12px",
                fontWeight: "700",
              },
            };
            l.dataLabels[i].formatter = function() {
              return (
                l.prefix +
                Highcharts.numberFormat(this.y, -1, ".", ",") +
                l.suffix
              );
            };
          });
        }
      });
    }
    if (self.userOptions.stack) {
      self.options.plotOptions.series.events = {};
      self.options.plotOptions.series.events.legendItemClick = function(e) {
        e.preventDefault();
      };
      if (!self.userOptions.stacking && self.userOptions.percent) {
        self.options.plotOptions.series.stacking = "percent";
      } else if (!self.userOptions.stacking) {
        self.options.plotOptions.series.stacking = "normal";
      } else {
        self.options.plotOptions.series.stacking = self.userOptions.stacking;
      }
    }
    if (self.userOptions.datetime) {
      let dates = Array.from(new Set(self.userOptions.dates));
      self.options.xAxis[0].type = "datetime";
      self.options.tooltip.xDateFormat = "%Y";
      if (dates.length < 16) {
        self.options.xAxis[0].tickPositions = dates;
        self.options.xAxis[0].labels = {
          formatter() {
            return Highcharts.dateFormat("%Y", this.value);
          },
        };
      }
    } else {
      self.options.xAxis[0].type = "category";
      self.options.xAxis[0].categories = self.userOptions.categories;
    }
    if (self.userOptions.type === "pie") {
      if (!self.options.plotOptions) self.options.plotOptions = {};
      if (!self.options.plotOptions.pie) self.options.plotOptions.pie = {};
      if (!self.options.plotOptions.pie.dataLabels)
        self.options.plotOptions.pie.dataLabels = {};
      self.options.plotOptions.pie.dataLabels.enabled = true;
      self.options.plotOptions.pie.dataLabels.formatter = function() {
        var sliceIndex = this.point.index;
        var sliceName = this.series.chart.axes[0].categories[sliceIndex];
        return sliceName;
      };
      self.options.tooltip.footerFormat = "";
      if (self.userOptions.percent) {
        self.options.tooltip.formatter = function() {
          var sliceIndex = this.point.index;
          var sliceName =
            '<table style="width: 100%;text-align: center">' +
            '<tr><td><span style="color: #112E51;font-size: 22px;font-weight: 500">' +
            this.point.y +
            '%</span></td></tr><tr><td><span style="color: #718297;font-size: 14px;font-weight: 400">' +
            this.series.chart.axes[0].categories[sliceIndex] +
            "</span></td></tr></table>";
          return sliceName;
        };
      } else {
        self.options.tooltip.formatter = function() {
          var sliceIndex = this.point.index;
          var sliceName =
            '<table style="width: 100%;text-align: center">' +
            '<tr><td><span style="color: #112E51;font-size: 22px;font-weight: 500">' +
            Highcharts.numberFormat(this.point.y, -1, ".", ",") +
            '</span></td></tr><tr><td><span style="color: #718297;font-size: 14px;font-weight: 400">' +
            this.series.chart.axes[0].categories[sliceIndex] +
            "</span></td></tr></table>";
          return sliceName;
        };
      }
    }

    if (self.userOptions.type === "bar") {
      if (!self.options.plotOptions.bar) self.options.plotOptions.bar = {};
      self.options.plotOptions.bar.negativeColor = "#EF233C";
      self.options.xAxis[0].labels.align = "right";
      self.options.xAxis[0].labels.reserveSpace = true;
      self.options.yAxis[0].tickAmount = 3;

      if (self.userOptions.series.length === 1) {
        self.options.legend.enabled = false;
      }

      if (self.userOptions.categories.length === 1) {
        self.options.chart.marginRight = 5;
        self.options.chart.marginLeft = 5;
        self.options.xAxis[0].labels.rotation = -90;
        self.options.xAxis[0].labels.align = "center";
        self.options.yAxis[0].labels.align = "center";
        self.options.yAxis[0].gridLineWidth = 1;
        self.options.yAxis[0].gridLineDashStyle = "dash";
        self.options.yAxis[0].tickColor = "#ccd6eb";
        self.options.yAxis[0].tickWidth = 0;
        self.options.yAxis[0].tickLength = 10;
        self.options.yAxis[0].tickPosition = "inside";
        self.options.yAxis[0].tickmarkPlacement = "between";
        self.options.xAxis[0].offset = 0;
        self.options.yAxis[0].offset = 0;
        self.options.yAxis[0].lineWidth = 0;
        self.options.xAxis[0].lineWidth = 3;
        self.options.yAxis[0].lineColor = "#ccd6eb";
        self.options.plotOptions.bar = {};

        self.options.plotOptions.bar.pointPadding = 0;
        self.options.plotOptions.bar.borderWidth = 0;
        self.options.plotOptions.bar.groupPadding = 0.1;
      }

      if (self.userOptions.categories.length > 1) {
        self.options.chart.marginRight = 5;
        self.options.xAxis[0].labels.align = "right";
        self.options.yAxis[0].labels.align = "center";
        self.options.yAxis[0].gridLineWidth = 1;
        self.options.yAxis[0].gridLineDashStyle = "dash";
        self.options.yAxis[0].tickColor = "#ccd6eb";
        self.options.yAxis[0].tickWidth = 0;
        self.options.yAxis[0].tickLength = 10;
        self.options.yAxis[0].tickPosition = "inside";
        self.options.yAxis[0].tickmarkPlacement = "between";
        self.options.yAxis[0].lineWidth = 0;
        self.options.xAxis[0].lineWidth = 3;
        self.options.yAxis[0].lineColor = "#ccd6eb";
        self.options.plotOptions.bar = {};

        self.options.plotOptions.bar.pointPadding = 0;
        self.options.plotOptions.bar.borderWidth = 0;
        self.options.plotOptions.bar.groupPadding = 0.1;
      }
    }

    if (self.userOptions.type === "column") {
      if (!self.options.plotOptions.column)
        self.options.plotOptions.column = {};
      // self.options.plotOptions.series.negativeColor = '#EF233C';
      self.options.xAxis[0].labels.align = "center";
      self.options.xAxis[0].labels.reserveSpace = true;
      self.options.yAxis[0].tickAmount = 3;

      if (self.userOptions.series.length === 1) {
        self.options.legend.enabled = false;
      }

      if (self.userOptions.categories.length === 1) {
        self.options.plotOptions.column = {};
        self.options.yAxis[0].showFirstLabel = true;
        self.options.yAxis[0].showLastLabel = true;

        self.options.xAxis[0].labels.align = "center";
        self.options.plotOptions.column.pointPadding = 0;
        self.options.plotOptions.column.borderWidth = 0;
        self.options.plotOptions.column.groupPadding = 0.1;
        self.options.xAxis[0].labels.enabled = false;
        self.options.xAxis[0].lineWidth = 3;
        self.options.xAxis[0].lineColor = "#ccd6eb";
        self.options.yAxis[0].gridLineWidth = 1;
        self.options.xAxis[0].gridLineWidth = 0;
        self.options.yAxis[0].labels.reserveSpace = false;
        self.options.yAxis[0].labels.align = "left";
        self.options.yAxis[0].labels.x = 0;
        self.options.yAxis[0].labels.y = -4;
        self.options.yAxis[0].offset = 0;
      }

      if (self.userOptions.categories.length > 1) {
        self.options.xAxis[0].labels.align = "center";
        self.options.yAxis[0].gridLineWidth = 1;
        self.options.yAxis[0].gridLineDashStyle = "dash";
        self.options.yAxis[0].tickColor = "#ccd6eb";
        self.options.yAxis[0].tickWidth = 0;
        self.options.yAxis[0].tickLength = 10;
        self.options.xAxis[0].lineWidth = 3;
        self.options.xAxis[0].lineColor = "#ccd6eb";
        self.options.yAxis[0].gridLineWidth = 1;
        self.options.xAxis[0].gridLineWidth = 0;
        self.options.yAxis[0].labels.reserveSpace = true;
        self.options.yAxis[0].labels.align = "right";
        self.options.yAxis[0].labels.x = -5;
        self.options.yAxis[0].labels.y = 4;
        self.options.yAxis[0].offset = 4;
        self.options.plotOptions.column.pointPadding = 0;
        self.options.plotOptions.column.borderWidth = 0;
        self.options.plotOptions.column.groupPadding = 0.1;
      }
    }

    if (
      self.userOptions.type === "line" ||
      self.userOptions.type === "spline"
    ) {
      self.options.xAxis[0].lineWidth = 3;
      self.options.xAxis[0].lineColor = "#ccd6eb";
      self.options.yAxis[0].gridLineWidth = 1;
      self.options.xAxis[0].gridLineWidth = 0;
      self.options.yAxis[0].labels.reserveSpace = true;
      self.options.yAxis[0].labels.align = "left";
      self.options.xAxis[0].labels.align = "center";
      self.options.yAxis[0].labels.x = 0;
      self.options.yAxis[0].labels.y = 4;
      self.options.yAxis[0].offset = 10;
      self.options.xAxis[0].offset = 0;
      self.options.yAxis[0].tickAmount = 3;
    }

    return self;
  }

  _setSeries() {
    const self = this;
    const series = [];
    const standby = [];
    const optsHeatmap = {
      colorAxis: { min: 0, minColor: "#FFFFFF", maxColor: "#ACDFF0" },
    };
    const optsRegression = {
      regression: true,
      regressionSettings: {
        type: "linear",
        dashStyle: "dash",
        hideInLegend: true,
        lineWidth: 1,
        name: "",
        tooltip: { enabled: false },
      },
    };

    this.userOptions.series.forEach(function(s, i) {
      let regression = {};
      let className = Highcharts.defined(s.className) ? s.className : "normal";
      let yAxis = Highcharts.defined(s.yAxis) ? s.yAxis : false;
      let xAxis = Highcharts.defined(s.xAxis) ? s.xAxis : false;
      let tooltip = Highcharts.defined(s.tooltip) ? s.tooltip : false;
      let dataLabels = Highcharts.defined(s.dataLabels) ? s.dataLabels : false;
      let defaults = {
        name: s.name,
        id: s.id,
        className: className,
        post: "",
        pre: "",
      };
      if (yAxis) defaults.yAxis = yAxis;
      if (xAxis) defaults.xAxis = xAxis;
      if (tooltip) defaults.tooltip = tooltip;
      if (dataLabels) defaults.dataLabels = dataLabels;
      if (s.color) defaults.color = s.color;
      if (s.post) defaults.post = s.post;
      if (s.pre) defaults.pre = s.pre;
      if (s.hidden) defaults.visible = false;
      if (s.name === "Annotate") {
        defaults.visible = false;
        defaults.enableMouseTracking = false;
        defaults.showInLegend = false;
      }
      s.data.forEach(function(d, i) {
        let data = d.values;
        let type = d.type;
        if (d.regression) regression = optsRegression;
        let merge = { ...regression, ...defaults };
        if (i === 0) {
          merge.data = data;
          merge.type = type;
          if (s.group) {
            merge.group = s.group;
            let defaultGroup = s.defaultGroup || false;
            if (defaultGroup) {
              if (
                String(defaultGroup).toLowerCase() ===
                String(s.group).toLowerCase()
              ) {
                self.userOptions.activeCollection = String(
                  s.group
                ).toLowerCase();
                series.push(merge);
              } else {
                standby.push(merge);
              }
            } else {
              let firstGroup = String(self.userOptions.groups[0]).toLowerCase();
              if (String(s.group).toLowerCase() === firstGroup) {
                self.userOptions.activeCollection = firstGroup.toLowerCase();
                series.push(merge);
              } else {
                standby.push(merge);
              }
            }
          } else if (d.set) {
            merge.group = d.set;
            let defaultToggle = s.defaultToggle || false;
            if (defaultToggle) {
              if (
                String(defaultToggle).toLowerCase() ===
                String(d.set).toLowerCase()
              ) {
                self.userOptions.activeCollection = String(d.set).toLowerCase();
                series.push(merge);
              } else {
                standby.push(merge);
              }
            } else {
              let firstToggle = String(
                self.userOptions.toggles[0]
              ).toLowerCase();
              if (String(d.set).toLowerCase() === firstToggle) {
                self.userOptions.activeCollection = firstToggle.toLowerCase();
                series.push(merge);
              } else {
                standby.push(merge);
              }
            }
          } else {
            series.push(merge);
          }
        } else {
          merge.data = data;
          merge.type = type;
          merge.group = d.set;
          let defaultToggle = s.defaultToggle || false;
          if (defaultToggle) {
            if (
              String(defaultToggle).toLowerCase() ===
              String(d.set).toLowerCase()
            ) {
              self.userOptions.activeCollection = String(d.set).toLowerCase();
              series.push(merge);
            } else {
              standby.push(merge);
            }
          } else {
            let firstToggle = String(self.userOptions.toggles[0]).toLowerCase();
            if (String(d.set).toLowerCase() === firstToggle) {
              self.userOptions.activeCollection = firstToggle.toLowerCase();
              series.push(merge);
            } else {
              standby.push(merge);
            }
          }
        }
      });
    });

    if (series.some((s) => s.name === "Annotate")) {
      if (self.userOptions.xAxis[0]) {
        self.options.yAxis = [
          { gridLineWidth: 0, labels: { style: {} }, title: { style: {} } },
          {
            gridLineWidth: 0,
            labels: { style: {} },
            title: { style: {} },
            opposite: true,
            visible: false,
          },
        ];
      } else {
        self.options.yAxis = [
          { gridLineWidth: 0, labels: { style: {} }, title: { style: {} } },
        ];
      }

      series.forEach(function(s) {
        if (s.name === "Annotate") {
          self.options.legend.enabled = false;
          self.options.yAxis[0].plotLines = [];
          s.data.forEach(function(d, i) {
            self.options.yAxis[0].plotLines[i] = {};
            self.options.yAxis[0].plotLines[i].value = d.y;
            self.options.yAxis[0].plotLines[i].zIndex = 1;
            self.options.yAxis[0].plotLines[i].color = `rgba(${hexToR(
              d
            )},${hexToG(d)},${hexToB(d)},1)`;
            self.options.yAxis[0].plotLines[i].dashStyle = "dot";
            self.options.yAxis[0].plotLines[i].width = 1;
            self.options.yAxis[0].plotLines[i].label = {};
            self.options.yAxis[0].plotLines[i].label.text =
              d.y + "%<br />" + d.category;
            self.options.yAxis[0].plotLines[i].label.align = "right";
            self.options.yAxis[0].plotLines[i].label.style = {};
            self.options.yAxis[0].plotLines[
              i
            ].label.style.color = `rgba(${hexToR(d)},${hexToG(d)},${hexToB(
              d
            )},1)`;
            self.options.yAxis[0].plotLines[i].label.style.fontWeight = "400";
            self.options.yAxis[0].plotLines[i].label.style.fontSize = "12px";
            self.options.yAxis[0].plotLines[i].label.x = 0;
            self.options.yAxis[0].plotLines[i].label.y = 15;
          });
        }
      });
    }

    this.series = series;
    this.standby = standby;
    this.combined = series.concat(standby);
    this.options.series = series;

    return this;
  }

  _initializeChart() {
    this.instance = Highcharts.chart(this.id_, this.options);

    const seriesArray = [];

    this.options.series.forEach(function(s) {
      s.data.forEach(function(d) {
        if (typeof d.y === "number") {
          seriesArray.push(d.y);
        }
      });
    });

    let max = stat.max([...seriesArray]);
    let min = stat.min([...seriesArray]);

    if (min > 0) min = 0;

    if (this.userOptions.stack && this.userOptions.percent) {
      this.instance.yAxis[0].setExtremes(0, 100);
    } else if (this.userOptions.stack) {
      let arr = [];
      sliceIntoChunks(
        seriesArray,
        seriesArray.length / this.options.series.length
      ).forEach((t) => arr.push(stat.max([...t])));
      this.instance.yAxis[0].setExtremes(
        min,
        this.userOptions.axisMax || stat.sum([...arr]) * 1.3
      );
    } else {
      this.instance.yAxis[0].setExtremes(min, max);
    }

    return this;
  }

  _addFilters() {
    const self = this;
    const chart = self.instance;

    if (self.standby.length > 0) {
      let filters = document.createElement("div");
      filters.classList.add("data-viz-filters");

      let container = document.createElement("div");
      container.classList.add("data-viz-buttons");

      self.options.chart.renderTo.parentElement.childNodes[0].appendChild(
        filters
      );
      filters.appendChild(container);

      const collection =
        self.userOptions.groups || self.userOptions.toggles || false;

      if (collection.length <= 3) {
        collection.forEach(function(el, i) {
          let button = document.createElement("button");
          let data = Array.from(
            self.combined.filter(
              (s) => String(s.group).toLowerCase() === String(el).toLowerCase()
            )
          );
          container.appendChild(button);
          button.classList.add("button");
          button.innerHTML = toTitleCase(el);

          if (String(el).toLowerCase() === self.userOptions.activeCollection)
            button.classList.add("active");

          button.addEventListener("click", function(e) {
            e.preventDefault();
            let seriesArray = [];
            data.forEach(function(s) {
              s.data.forEach(function(d) {
                if (typeof d.y === "number") {
                  seriesArray.push(d.y);
                }
              });
            });

            let max = stat.max([...seriesArray]);
            let min = stat.min([...seriesArray]);

            if (min > 0) min = 0;

            if (self.userOptions.stack && self.userOptions.percent) {
              self.instance.yAxis[0].setExtremes(0, 100);
            } else if (self.userOptions.stack) {
              let arr = [];
              sliceIntoChunks(
                seriesArray,
                seriesArray.length / self.options.series.length
              ).forEach((t) => arr.push(stat.max([...t])));
              self.instance.yAxis[0].setExtremes(min, stat.sum([...arr]) * 1.3);
            } else {
              self.instance.yAxis[0].setExtremes(min, max);
            }

            if (e.target.classList.contains("active")) {
              return false;
            } else {
              this.parentElement
                .querySelectorAll(".data-viz-buttons button")
                .forEach((btn) => {
                  btn.setAttribute("class", "button");
                });
              e.target.classList.add("active");
            }
            for (var i = chart.series.length - 1; i >= 0; i--) {
              chart.series[i].remove(false);
            }
            for (let d of data) {
              chart.addSeries(d);
            }
          });
        });
      } else {
        let select = document.createElement("select");
        container.appendChild(select);
        collection.forEach(function(el, i) {
          let option = document.createElement("option");
          select.appendChild(option);
          option.innerHTML = toTitleCase(el);
          if (String(el).toLowerCase() === self.userOptions.activeCollection)
            option.selected = true;
        });
        select.addEventListener("change", function(e) {
          let data = Array.from(
            self.combined.filter(
              (s) =>
                String(s.group).toLowerCase() === e.target.value.toLowerCase()
            )
          );
          let seriesArray = [];
          data.forEach(function(s) {
            s.data.forEach(function(d) {
              if (typeof d.y === "number") {
                seriesArray.push(d.y);
              }
            });
          });

          let max = stat.max([...seriesArray]);
          let min = stat.min([...seriesArray]);

          if (min > 0) min = 0;

          if (self.userOptions.stack && self.userOptions.percent) {
            self.instance.yAxis[0].setExtremes(0, 100);
          } else if (self.userOptions.stack) {
            let arr = [];
            sliceIntoChunks(
              seriesArray,
              seriesArray.length / self.options.series.length
            ).forEach((t) => arr.push(stat.max([...t])));
            self.instance.yAxis[0].setExtremes(min, stat.sum([...arr]) * 1.3);
          } else {
            self.instance.yAxis[0].setExtremes(min, max);
          }

          for (var i = chart.series.length - 1; i >= 0; i--) {
            chart.series[i].remove(false);
          }
          for (let d of data) {
            chart.addSeries(d);
          }
        });
      }
    }
    return this;
  }

  get live() {
    return this.instance;
  }

  update(opts) {
    return this.instance.update(opts);
  }

  static lookup(title) {
    let c = Charts.find((c, i) => c.title_ === title);
    return c ? c.live : undefined;
  }
}
/* eslint-enable */

export default Chart;
